/**
 * Panel — the scifi chrome wrapper used everywhere in the app + overlay.
 *
 * Two-layer trick so the border + interior compose without an SVG: outer
 * fills the border color and is clipped to the notched silhouette; inner
 * sits inset by 1px and is clipped to a marginally tighter notch so the
 * outer hairline reads cleanly at the chamfered edges.
 *
 * Variants:
 *   - `hairline` (default) — see-through retro panel; interior uses
 *     `--pp-bg` (defaults to a translucent dark fill so content reads
 *     over busy 3D scenes; set `--pp-bg: transparent` for a fully
 *     hollow ring).
 *   - `solid` — fills the interior with `--pp-bg-strong` for the top
 *     metadata strip, modals, accolade flash, and similar pinned chrome.
 *
 * The optional `accent` prop paints a 2px stripe along the top edge of
 * the interior — used by side-A/side-B stat panels and by the accolade
 * flash to colour-code severity.
 *
 * Pointer events: this primitive does NOT set `pointer-events: none`.
 * Consumers compose that on their root wrapper when needed (the
 * overlay roots already do). Audit fix — Panel used to force
 * pointer-events:none on the outer wrapper, which double-locked any
 * scrollable / interactive descendant (the ReasoningPanel scroll bug).
 *
 * HTML attribute passthrough: extra `HTMLAttributes<HTMLDivElement>` are
 * forwarded to the outer wrapper (className, onClick, role, id, etc.).
 * Use `ref` to grab the outer element. Necessary so consumer components
 * like `<Card>` and `<DialogContent>` can compose Panel as their root.
 */

import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { DEFAULT_CHROME_TOKENS } from "./tokens.js";
import { notchedClipPath, parsePx } from "./notch.js";

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Panel body. Optional so consumer components like `<Card>` can
   *  pass through ReactNode children that may be undefined; rendering
   *  an empty Panel produces just the chamfered chrome ring. */
  children?: ReactNode;
  /** Override the corner notch in px. Defaults to the `--pp-corner-notch`
   *  token's numeric value. */
  notch?: number;
  /** Optional accent color for the top inset stripe (side accent). */
  accent?: string;
  /** Visual variant. */
  variant?: "hairline" | "solid";
  /** ARIA label forwarded to the outer wrapper. */
  ariaLabel?: string;
  /** Style merged onto the INNER wrapper (where content lives). Use the
   *  standard `style` prop for the outer wrapper's positioning. */
  innerStyle?: CSSProperties;
}

const outerBase: CSSProperties = {
  padding: 1,
  background: "var(--pp-border)",
};

const innerBaseHairline: CSSProperties = {
  background: "var(--pp-bg)",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
};

const innerBaseSolid: CSSProperties = {
  background: "var(--pp-bg-strong)",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
};

const accentStripeBase: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 2,
  pointerEvents: "none",
};

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    children,
    style,
    innerStyle,
    notch,
    accent,
    variant = "hairline",
    ariaLabel,
    ...rest
  },
  ref,
) {
  const notchPx = notch ?? parsePx(DEFAULT_CHROME_TOKENS.cornerNotch, 10);
  const outerClip = notchedClipPath(notchPx);
  // Inner clip is one px tighter at each axis so the outer-color
  // hairline rings the inner fill at the chamfered edges too.
  const innerClip = notchedClipPath(Math.max(0, notchPx - 1));
  const innerBase = variant === "solid" ? innerBaseSolid : innerBaseHairline;
  return (
    <div
      ref={ref}
      style={{ ...outerBase, clipPath: outerClip, ...style }}
      aria-label={ariaLabel}
      data-pp-panel
      data-pp-variant={variant}
      {...rest}
    >
      <div
        style={{
          ...innerBase,
          clipPath: innerClip,
          position: "relative",
          ...innerStyle,
        }}
      >
        {accent ? <div style={{ ...accentStripeBase, background: accent }} /> : null}
        {children}
      </div>
    </div>
  );
});
